// Route-level role gate.
//
// Wrap a route element in <RequireRole roles={['manager']}> to block
// non-manager reps from reaching Team Live, Executive Live, and the
// coaching / manager rails. The gate is a UI check, not the only check:
// api-server endpoints still verify the rep's HubSpot role on every
// write; this component only avoids rendering a surface the rep is not
// authorized to see.

import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRep, type RepRole } from './useRep.js';
import { WorkspaceChrome, type RailKey } from './WorkspaceChrome.js';

export interface RequireRoleProps {
  roles: readonly RepRole[];
  rail?: RailKey;
  children: ReactNode;
}

export function RequireRole({
  roles,
  rail = 'my-work',
  children,
}: RequireRoleProps): ReactNode {
  const state = useRep();
  const navigate = useNavigate();
  const location = useLocation();

  if (state.status === 'loading') {
    return (
      <WorkspaceChrome rail={rail}>
        <section
          className="card"
          style={{ padding: 24 }}
          aria-labelledby="role-gate-loading"
          aria-busy="true"
        >
          <span className="eyebrow">LOADING</span>
          <h1 id="role-gate-loading">Checking your access…</h1>
          <p role="status">Waiting for your HubSpot session.</p>
        </section>
      </WorkspaceChrome>
    );
  }
  if (state.status === 'unauthenticated') {
    return (
      <WorkspaceChrome rail={rail}>
        <section className="card" style={{ padding: 24 }} aria-labelledby="role-gate-unauth">
          <span className="eyebrow">SIGN-IN REQUIRED</span>
          <h1 id="role-gate-unauth">Open this workspace from HubSpot.</h1>
          <p>
            This surface requires an authenticated session. Return to a
            HubSpot record and launch the Sales Workspace from the sidebar.
          </p>
        </section>
      </WorkspaceChrome>
    );
  }
  if (!roles.includes(state.rep.role)) {
    return (
      <WorkspaceChrome rail={rail}>
        <section
          className="card"
          style={{ padding: 24 }}
          aria-labelledby="role-gate-denied"
        >
          <span className="eyebrow">NOT AVAILABLE FOR YOUR ROLE</span>
          <h1 id="role-gate-denied">Not available for your role.</h1>
          <p>
            {displayDenied(location.pathname, state.rep.role, roles)} If you
            think you should have access, your manager can update your role
            in HubSpot.
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => navigate('/')}
          >
            Back to queue
          </button>
        </section>
      </WorkspaceChrome>
    );
  }
  return <>{children}</>;
}

function displayDenied(
  pathname: string,
  actual: RepRole,
  required: readonly RepRole[],
): string {
  const surface = pathname.replace(/^\//, '').replace(/[/?#].*$/, '') || 'this surface';
  const requiredList = required.join(' or ');
  return `${humanisePath(surface)} is reserved for ${requiredList}; your role is ${actual}.`;
}

function humanisePath(path: string): string {
  switch (path) {
    case 'team-live':
      return 'Team Live';
    case 'executive-live':
      return 'Executive Live';
    case 'manager':
      return 'The manager rail';
    case 'coaching':
      return 'Coaching';
    default:
      return path;
  }
}
